import React, { useEffect, useMemo, useState } from 'react';
import { Button, Drawer, TimePicker, message, Radio, Select, Spin, Row, Col } from 'antd';
import { Link } from 'react-router-dom';
import dayjs, { Dayjs } from 'dayjs';
import { useDispatch, useSelector } from 'react-redux';
import axios from 'axios';

import { createShiftMaster, fetchAllShiftMasters, updateShiftMaster, deleteShiftMaster } from '../../../core/data/redux/attendance/shift/shiftMasterAction';
import DeleteModal1 from '../../../core/modals/deleteModal1';
import type { ShiftMaster } from '../../../core/data/redux/attendance/shift/shiftMasterType';
import { AppDispatch } from '../../../core/data/redux/store';
import Table from "../../../core/common/dataTable/index";
import CollapseHeader from '../../../core/common/collapse-header/collapse-header';
import { exportToCSV, exportToExcel, exportToPDF } from '../../../core/common/GlobalFunctions/exportTableData';
import { all_routes } from '../../router/all_routes';
import { filterData } from '../../../core/common/GlobalFunctions/tableFilters';
import { decryptData } from '../../../core/utils/encryptUtils';
import timeZones from '../../administration/timezone';
import { useAppConfig } from '../../../customhooks/useAppConfig';
import { formatPlainTime, formatTimeByZone } from '../../../core/utils/configFormatters';

const COUNTRY_API = 'https://partnerapi.leadchainsystems.in/api/v1/countries';
const COUNTRY_API_TOKEN = 'Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1aWQiOiI5NjUwYWU5MC03YjRmLTQ5MWUtODVlOC1iMjM1ZmQyODlkMDAiLCJpYXQiOjE3MzQzNDczODl9.XWRyYTdl_10enuZkBCK1-WInCOGmiGW5PKZzJelrobY';

const normalizeCountry = (name: string) => {
    const map: Record<string, string> = {
        'Brunei Darussalam': 'Brunei',
        'Cabo Verde': 'Cabo Verde',
        'Aland Islands': 'Åland Islands',
        'United States of America': 'United States',
    };
    return map[name] ?? name;
};

const ShiftMasterPage = () => {
    const dispatch = useDispatch<AppDispatch>();
    const { shiftMasters = [], loadingStates } = useSelector((state: any) => state.attendance.shift);
    const isCreating = loadingStates.create;
    const isUpdating = loadingStates.update;
    const [selectedSort, setSelectedSort] = useState<any>("");
    const [tableKey, setTableKey] = useState(0);
    const { user } = useSelector((state: any) => state.auth);

    const [drawerVisible, setDrawerVisible] = useState(false);
    const [modalType, setModalType] = useState<'create' | 'edit' | ''>('');
    const [selectedShift, setSelectedShift] = useState<ShiftMaster | null>(null);

    // --- Existing shift fields ---
    const [formData, setFormData] = useState<{ ShiftName: string; StartTime: Dayjs | null; EndTime: Dayjs | null; BreakTime: Dayjs | null; }>({ ShiftName: '', StartTime: null, EndTime: null, BreakTime: null });

    // --- New: Mode + Location fields ---
    const [locationMode, setLocationMode] = useState<'shift' | 'location'>('shift'); // default Shift based
    const [countries, setCountries] = useState<{ name: string; iso_code: string }[]>([]);
    const [loadingCountries, setLoadingCountries] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<string>('');
    const [countryTimeZones, setCountryTimeZones] = useState<string[]>([]);
    const [selectedTimeZone, setSelectedTimeZone] = useState<string>('');

    const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});
    const [deleteVisible, setDeleteVisible] = useState(false);

    const encryptedPermissions = localStorage.getItem('currentPermissions');
    const permissions: string[] = Array.isArray(decryptData(encryptedPermissions || '')) ? decryptData(encryptedPermissions || '') : [];
    const canAdd = permissions.includes("Add") || permissions.includes("AllowAll");
    const canEdit = permissions.includes("Edit") || permissions.includes("AllowAll");
    const canDelete = permissions.includes("Delete") || permissions.includes("AllowAll");
    const canExport = permissions.includes("Export") || permissions.includes("AllowAll");

    const config = useAppConfig();

    useEffect(() => {
        dispatch(fetchAllShiftMasters());
    }, [dispatch]);

    // Fetch countries once (when component mounts) so Location based can use them
    useEffect(() => {
        const fetchCountries = async () => {
            try {
                setLoadingCountries(true);
                const response = await axios.get(COUNTRY_API, {
                    headers: { Authorization: COUNTRY_API_TOKEN },
                });
                setCountries(Array.isArray(response.data) ? response.data : []);
            } catch (error) {
                console.error('Error fetching countries:', error);
                message.error('Failed to fetch country list.');
            } finally {
                setLoadingCountries(false);
            }
        };
        fetchCountries();
    }, []);

    const resetForm = () => {
        setFormData({ ShiftName: '', StartTime: null, EndTime: null, BreakTime: null });
        setValidationErrors({});
        setSelectedShift(null);

        // reset location-specific
        setLocationMode('shift');
        setSelectedCountry('');
        setCountryTimeZones([]);
        setSelectedTimeZone('');
    };

    const openDrawer = (mode: 'create' | 'edit', shift?: any) => {
        setModalType(mode);
        if (mode === 'edit' && shift) {
            setSelectedShift(shift);
            setFormData({
                ShiftName: shift.ShiftName,
                StartTime: dayjs(shift.StartTime, 'HH:mm:ss'),
                EndTime: dayjs(shift.EndTime, 'HH:mm:ss'),
                BreakTime: dayjs(shift.BreakTime, 'HH:mm:ss')
            });

            // If your existing record carries location fields, populate here
            const recordMode = shift.LocationMode === 'location' ? 'location' : 'shift';
            setLocationMode(recordMode);
            setSelectedCountry(shift.CountryName || '');
            setSelectedTimeZone(shift.TimeZone || '');

            // Pre-compute TZ list if needed
            const n = normalizeCountry(shift.CountryName || '');
            const match = timeZones.find(tz => tz.country === n);
            setCountryTimeZones(match ? match.timeZones : []);
        } else {
            resetForm();
        }
        setDrawerVisible(true);
    };

    const closeDrawer = () => {
        setDrawerVisible(false);
        setModalType('');
        resetForm();
    };

    const handleFormChange = (field: keyof typeof formData, value: any) => {
        setFormData(prev => ({ ...prev, [field]: value }));
    };

    // When country changes, compute its time zones from the provided list
    useEffect(() => {
        if (!selectedCountry) {
            setCountryTimeZones([]);
            setSelectedTimeZone('');
            return;
        }

        const normalized = normalizeCountry(selectedCountry);
        const match = timeZones.find(tz => tz.country === normalized);
        const zones = match?.timeZones ?? [];
        setCountryTimeZones(zones);

        // ✅ Keep previously selected value if it’s still valid
        setSelectedTimeZone((prev) => (prev && zones.includes(prev) ? prev : ''));
    }, [selectedCountry]);


    const validate = () => {
        const e: Record<string, string> = {};

        if (!formData.ShiftName.trim()) e.ShiftName = 'Shift Name is required.';
        if (!formData.StartTime) e.StartTime = 'Start Time is required.';
        if (!formData.EndTime) e.EndTime = 'End Time is required.';
        if (!formData.BreakTime) e.BreakTime = 'Break Hours are required.';

        if (
            formData.StartTime &&
            formData.EndTime &&
            (formData.StartTime.isSame(formData.EndTime) || formData.StartTime.isAfter(formData.EndTime))
        ) {
            e.time = 'Start Time must be earlier than End Time.';
        }

        // Extra validation when "Location based"
        if (locationMode === 'location') {
            if (!selectedCountry) e.CountryName = 'Country is required for Location based.';
            // You could also allow no TZ and default later; here we require it
            if (!selectedTimeZone) e.TimeZone = 'Time Zone is required for Location based.';
        }

        setValidationErrors(e);
        return Object.keys(e).length === 0;
    };

    const handleSubmit = async () => {
        if (!validate()) return;

        const payload = {
            ShiftName: formData.ShiftName,
            StartTime: formData.StartTime!.format('HH:mm:ss'),
            EndTime: formData.EndTime!.format('HH:mm:ss'),
            BreakTime: formData.BreakTime!.format('HH:mm:ss'),
            // New fields:
            LocationMode: locationMode,                 // 'shift' | 'location'
            CountryName: locationMode === 'location' ? selectedCountry : null,
            TimeZone: locationMode === 'location' ? selectedTimeZone : null,

            CreatedBy: user.UserId,
            ModifiedBy: user.UserId,
        };

        try {
            if (modalType === 'create') {
                await dispatch(createShiftMaster(payload)).unwrap();
                message.success('Shift created successfully');
            } else if (modalType === 'edit' && selectedShift) {
                await dispatch(updateShiftMaster({ shiftId: selectedShift.ShiftID, data: payload })).unwrap();
                message.success('Shift Updated successfully');
            }
            dispatch(fetchAllShiftMasters());
            closeDrawer();
        } catch (err: any) {
            message.error(err?.error || 'Operation failed');
        }
    };

    const confirmDelete = async () => {
        if (!selectedShift) return;
        try {
            await dispatch(deleteShiftMaster(selectedShift.ShiftID)).unwrap();
            message.success('Deleted shift successfully');
            dispatch(fetchAllShiftMasters());
            setDeleteVisible(false);
        } catch (err: any) {
            console.log("err.error", err.error)
            message.error(err.error || 'Delete failed');
        }
    };

    const filteredData: any = useMemo(() => {
        return filterData(shiftMasters || [], {}, "CreatedAt", "CreatedAt", selectedSort);
    }, [shiftMasters, selectedSort]);

    const exportData = filteredData.map((data: any) => ({
        ShiftID: data.ShiftID,
        ShiftName: data.ShiftName,
        StartTime: data.StartTime ? formatPlainTime(data.StartTime, config) : null,
        EndTime: data.EndTime ? formatPlainTime(data.EndTime, config) : null,
        BreakTime: data.BreakTime ? dayjs(`2000-01-01T${data.BreakTime}`).format('HH:mm') : null,
        LocationMode: data.LocationMode || 'shift',
        CountryName: data.CountryName || 'NA',
        TimeZone: data.TimeZone || 'NA',
        CreatedBy: data.CreatedByName || '',
        ModifydBy: data.ModifiedByName || '',
    }));

    useEffect(() => {
        setTableKey(prevKey => prevKey + 1);
    }, [selectedSort, shiftMasters]);

    const columns = [
        {
            title: 'Shift Name',
            dataIndex: 'ShiftName',
            sorter: (a: any, b: any) => a.ShiftName.localeCompare(b.ShiftName),
        },
        {
            title: 'Start Time',
            dataIndex: 'StartTime',
            render: (value: string, record: any) => {
                const plain = formatPlainTime(value, config);
                const zoned = formatTimeByZone(value, config, record?.TimeZone ?? config?.timezone);

                return (
                    <span>
                        {plain}
                        {record?.TimeZone && zoned ? (
                            <span style={{ color: '#888' }}>({zoned})</span>
                        ) : null}
                    </span>
                );
            },
            sorter: (a: any, b: any) => a.StartTime.localeCompare(b.StartTime),
        },
        {
            title: 'End Time',
            dataIndex: 'EndTime',
            render: (value: string, record: any) => {
                const plain = formatPlainTime(value, config);
                const zoned = formatTimeByZone(value, config, record?.TimeZone ?? config?.timezone);
                return (
                    <span>
                        {plain} {zoned ? <span style={{ color: '#888' }}>({zoned})</span> : null}
                    </span>
                );
            },
            sorter: (a: any, b: any) => a.EndTime.localeCompare(b.EndTime),
        },
        {
            title: 'Break Hours',
            dataIndex: 'BreakTime',
            sorter: (a: any, b: any) => a.BreakTime.localeCompare(b.BreakTime),
        },
        {
            title: 'Mode',
            dataIndex: 'LocationMode',
            render: (v: any) => (v === 'location' ? 'Location based' : 'Shift based'),
        },
        {
            title: 'Country',
            dataIndex: 'CountryName',
            render: (value: any) => value ? value : 'NA',
        },
        {
            title: 'Time Zone',
            dataIndex: 'TimeZone',
            render: (value: any) => value ? value : 'NA',
        },
        {
            title: 'Actions',
            dataIndex: 'actions',
            render: (_: any, record: ShiftMaster) => (
                <div className="action-icon d-inline-flex">
                    <button
                        className="me-2"
                        onClick={() => openDrawer('edit', record)}
                        data-bs-toggle="tooltip"
                        data-bs-placement="top"
                        title={canEdit ? "Edit" : "No permission to edit"}
                        disabled={!canEdit}
                        style={{ cursor: canEdit ? "pointer" : "not-allowed" }}
                    >
                        <i className="ti ti-edit" />
                    </button>
                    <button
                        onClick={() => { setSelectedShift(record); setDeleteVisible(true); }}
                        data-bs-toggle="tooltip"
                        disabled={!canDelete}
                        style={{ cursor: canDelete ? "pointer" : "not-allowed" }}
                        data-bs-placement="top"
                        title={canEdit ? "Delete" : "No permission to Delete"}
                    >
                        <i className="ti ti-trash" />
                    </button>
                </div>
            ),
        },
    ];

    return (
        <div className="page-wrapper">
            <div className="content">
                <div>
                    <div className="d-md-flex d-block align-items-center justify-content-between page-breadcrumb mb-3">
                        <div className="my-auto mb-2">
                            <h2 className="mb-1">Shifts</h2>
                            <nav>
                                <ol className="breadcrumb mb-0">
                                    <li className="breadcrumb-item">
                                        <Link to={all_routes.adminDashboard}>
                                            <i className="ti ti-smart-home" />
                                        </Link>
                                    </li>
                                    <li className="breadcrumb-item">Attendance</li>
                                    <li className="breadcrumb-item active" aria-current="page">
                                        Shift
                                    </li>
                                </ol>
                            </nav>
                        </div>
                        <div className="d-flex my-xl-auto right-content align-items-center flex-wrap ">
                            <div className="me-2 mb-2">
                                <div className="dropdown">
                                    {canExport && (
                                        <Link
                                            to="#"
                                            className="dropdown-toggle btn btn-white d-inline-flex align-items-center"
                                            data-bs-toggle="dropdown"
                                        >
                                            <i className="ti ti-file-export me-1" />
                                            Export
                                        </Link>
                                    )}            
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li>
                                            <button onClick={() => exportToCSV(exportData, 'shift_data')} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-pdf me-1" />
                                                Export as CSV
                                            </button>
                                        </li>
                                        <li>
                                            <button onClick={() => exportToExcel(exportData, 'shift_data')} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as Excel{" "}
                                            </button>
                                        </li>
                                        <li>
                                            <button onClick={() => exportToPDF(exportData, 'shift_data')} className="dropdown-item rounded-1">
                                                <i className="ti ti-file-type-xls me-1" />
                                                Export as PDF{" "}
                                            </button>
                                        </li>
                                    </ul>
                                </div>
                            </div>
                            <div className="mb-2">
                                {canAdd && (
                                    <button
                                        onClick={() => openDrawer('create')}
                                        className="btn btn-primary d-flex align-items-center"
                                    >
                                        <i className="ti ti-circle-plus me-2" />
                                        Add Shift
                                    </button>
                                )}
                            </div>

                            <div className="ms-2 head-icons">
                                <CollapseHeader />
                            </div>
                        </div>
                    </div>

                    <div className="card">
                        <div className="card-header d-flex align-items-center justify-content-between flex-wrap row-gap-3">
                            <h5>Shift List</h5>
                            <div className="d-flex my-xl-auto right-content align-items-center flex-wrap row-gap-3">
                                <div className="dropdown">
                                    <Link to="#" className="dropdown-toggle btn btn-sm btn-white d-inline-flex align-items-center" data-bs-toggle="dropdown">
                                        {selectedSort ? `Sort By : ${selectedSort}` : "Sort By : Last 7 Days"}
                                    </Link>
                                    <ul className="dropdown-menu dropdown-menu-end p-3">
                                        <li><Link to="#" onClick={() => setSelectedSort("Recently Added")} className="dropdown-item rounded-1">Recently Added</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Ascending")} className="dropdown-item rounded-1">Ascending</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Descending")} className="dropdown-item rounded-1">Descending</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Last Month")} className="dropdown-item rounded-1">Last Month</Link></li>
                                        <li><Link to="#" onClick={() => setSelectedSort("Last 7 Days")} className="dropdown-item rounded-1">Last 7 Days</Link></li>
                                    </ul>
                                </div>
                            </div>
                        </div>
                        <div className="card-body p-0">
                            <Table key={tableKey} dataSource={filteredData} columns={columns} Selection={false} />
                        </div>
                    </div>

                    <Drawer
                        title={modalType === 'create' ? 'New Shift' : 'Edit Shift'}
                        width={620}
                        open={drawerVisible}
                        onClose={closeDrawer}
                        style={{ marginTop: 50, paddingBottom: 50 }}
                    >
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {/* Mode selector (full width) */}
                            <label>Shift Assignment Mode</label>
                            <Radio.Group value={locationMode} onChange={(e) => setLocationMode(e.target.value)}>
                                <Radio value="shift">Shift based</Radio>
                                <Radio value="location">Location based</Radio>
                            </Radio.Group>

                            {/* Location fields only when location-based */}
                            {locationMode === 'location' && (
                                <>
                                    <Row gutter={12}>
                                        <Col xs={24} md={12}>
                                            <label>
                                                Country <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <Select
                                                showSearch
                                                placeholder="Select country"
                                                value={selectedCountry || undefined}
                                                onChange={(v) => setSelectedCountry(v)}
                                                filterOption={(input, option) =>
                                                    (option?.label as string).toLowerCase().includes(input.toLowerCase())
                                                }
                                                options={countries.map(c => ({ label: c.name, value: c.name }))}
                                                style={{ width: '100%' }}
                                                notFoundContent={loadingCountries ? <Spin size="small" /> : null}
                                            />
                                            {validationErrors.CountryName && (
                                                <div style={{ color: 'red' }}>{validationErrors.CountryName}</div>
                                            )}
                                        </Col>

                                        <Col xs={24} md={12}>
                                            <label>
                                                Time Zone <span style={{ color: 'red' }}>*</span>
                                            </label>
                                            <Select
                                                placeholder={selectedCountry ? 'Select time zone' : 'Select a country first'}
                                                value={selectedTimeZone || undefined}
                                                onChange={(v) => setSelectedTimeZone(v)}
                                                disabled={!selectedCountry || countryTimeZones.length === 0}
                                                options={countryTimeZones.map(z => ({ label: z, value: z }))}
                                                style={{ width: '100%' }}
                                            />
                                            {countryTimeZones.length === 0 && selectedCountry && (
                                                <div style={{ color: '#a67c00' }}>
                                                    No predefined time zones found for "{selectedCountry}". Please update your time zone list if needed.
                                                </div>
                                            )}
                                            {validationErrors.TimeZone && (
                                                <div style={{ color: 'red' }}>{validationErrors.TimeZone}</div>
                                            )}
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {/* Shift fields in two-per-row */}
                            <Row gutter={12}>
                                <Col xs={24} md={12}>
                                    <label>
                                        Shift Name <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <input
                                        type="text"
                                        className="form-control"
                                        value={formData.ShiftName}
                                        onChange={e => handleFormChange('ShiftName', e.target.value)}
                                    />
                                    {validationErrors.ShiftName && <div style={{ color: 'red' }}>{validationErrors.ShiftName}</div>}
                                </Col>

                                <Col xs={24} md={12}>
                                    <label>
                                        Start Time <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <TimePicker
                                        value={formData.StartTime}
                                        format="HH:mm"
                                        onChange={v => handleFormChange('StartTime', v)}
                                        style={{ width: '100%' }}
                                    />
                                    {validationErrors.StartTime && <div style={{ color: 'red' }}>{validationErrors.StartTime}</div>}
                                </Col>
                            </Row>

                            <Row gutter={12}>
                                <Col xs={24} md={12}>
                                    <label>
                                        End Time <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <TimePicker
                                        value={formData.EndTime}
                                        format="HH:mm"
                                        onChange={v => handleFormChange('EndTime', v)}
                                        style={{ width: '100%' }}
                                    />
                                    {validationErrors.EndTime && <div style={{ color: 'red' }}>{validationErrors.EndTime}</div>}
                                </Col>

                                <Col xs={24} md={12}>
                                    <label>
                                        Break Hours <span style={{ color: 'red' }}>*</span>
                                    </label>
                                    <TimePicker
                                        format="HH:mm"
                                        value={formData.BreakTime}
                                        onChange={v => handleFormChange('BreakTime', v)}
                                        minuteStep={5}
                                        allowClear={false}
                                        defaultOpenValue={dayjs('00:00', 'HH:mm')}
                                        disabledHours={() => [...Array(21).keys()].map(i => i + 3)} // disable hours > 2
                                        disabledMinutes={(selectedHour) =>
                                            selectedHour === 3 ? [5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55] : []
                                        }
                                        style={{ width: '100%' }}
                                    />
                                    {validationErrors.BreakTime && <div style={{ color: 'red' }}>{validationErrors.BreakTime}</div>}
                                </Col>
                            </Row>

                            {validationErrors.time && <div style={{ color: 'red' }}>{validationErrors.time}</div>}

                            <div style={{ display: 'flex', justifyContent: 'center', marginTop: 8 }}>
                                <Button
                                    type="primary"
                                    disabled={modalType === 'create' ? isCreating : isUpdating}
                                    onClick={handleSubmit}
                                    style={{ width: 160 }}
                                >
                                    {(modalType === 'create' ? isCreating : isUpdating) && (
                                        <span className="spinner-border spinner-border-sm me-2" />
                                    )}
                                    {modalType === 'create' ? 'Create' : 'Update'}
                                </Button>
                            </div>
                        </div>
                    </Drawer>


                    <DeleteModal1
                        isVisible={deleteVisible}
                        onClose={() => setDeleteVisible(false)}
                        onConfirm={confirmDelete}
                    />
                </div>
            </div>
        </div>
    );
};

export default ShiftMasterPage;
